import { useCallback, useEffect } from 'react';
import { useAuthStore } from '../store';
import { conversationKey, isConversationUnread, useChatReadStore } from '../store/chatRead';
import type { ConversationKind } from '../store/chatRead';
import type { GroupConversation, TaskConversation } from '../types/conversations';

// Turns the fetched conversation lists into per-conversation unread flags
// using the local read state (see store/chatRead.ts).
export function useChatUnread(
  taskConversations: TaskConversation[],
  groupConversations: GroupConversation[],
  loaded: boolean
) {
  const userId = useAuthStore((s) => s.user?.id);
  const state = useChatReadStore((s) => (userId ? s.byUser[userId] : undefined));
  const markReadStore = useChatReadStore((s) => s.markRead);
  const baseline = useChatReadStore((s) => s.baseline);

  // First ever sighting of this user's conversations: treat them all as read.
  useEffect(() => {
    if (!userId || !loaded || state?.initialized) return;
    const latest: Record<string, string> = {};
    taskConversations.forEach((c) => { latest[conversationKey('task', c.id)] = c.latest_message.created_at; });
    groupConversations.forEach((c) => { latest[conversationKey('group', c.id)] = c.latest_message.created_at; });
    baseline(userId, latest);
  }, [userId, loaded, state?.initialized, taskConversations, groupConversations, baseline]);

  const isUnread = useCallback(
    (kind: ConversationKind, item: { id: string; latest_message: { author_id: string; created_at: string } }) =>
      isConversationUnread(state, conversationKey(kind, item.id), item.latest_message, userId),
    [state, userId]
  );

  const markRead = useCallback(
    (kind: ConversationKind, id: string, at?: string) => {
      if (userId) markReadStore(userId, conversationKey(kind, id), at);
    },
    [userId, markReadStore]
  );

  const unreadCount =
    taskConversations.filter((c) => isUnread('task', c)).length +
    groupConversations.filter((c) => isUnread('group', c)).length;

  return { isUnread, markRead, unreadCount };
}
